import React, { Fragment } from 'react';
import { FormattedMessage } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Tooltip from '../../common/tooltip/Tooltip';
import { DATE_FORMAT3 } from '@utils/dates/dateMethods';
import { formatDate } from '@utils/dates/date.utils';
import { useExchangeConditions } from '@hooks/useExchangeConditions';
import { ConditionalRendering } from '@components/common/conditional-rendering';
import { formatDecimalNumber } from '@utils/miscellaneous/formatNumber';

const EligibilityOverviewListItem = ({ item }) => {
    return (
        <li className="eligibility-overview__list-item">
            <FontAwesomeIcon icon="check" size="1x" className="eligibility-overview__list-item__icon" />
            {item}
        </li>
    );
};

const EligibilityOverviewList = ({ list }) => {

    return (
        <ul className="eligibility-overview__list">
            {list.map((item, idx) => {
                return <EligibilityOverviewListItem key={idx} item={item} />;
            })}
        </ul>
    );
};

const EligibilityOverview = (props) => {

    const {
        applicationDate,
        caseNumber,
        isFinancial,
        isState,
        data: eligibilityOveriewData,
        coverageYear,
        additionalNote,
        showSummaryHeader,
        taxHouseholdDetails,
        isMaintainFATextVisible,
        medicaidFullName,
        exchangeName,
        isBHPEligibleTextVisible,
    } = props;

    const {
        orderedEligibilityList,
    } = eligibilityOveriewData;

    const { isEligibilityMaintainTextVisible } = useExchangeConditions();

    const orderedEligibilityListHasValue = Object.entries(orderedEligibilityList).some(([, value]) => value.length);
    const formattedApplicationDate = applicationDate && formatDate(applicationDate, DATE_FORMAT3);
    return (
        <div className="eligibility-overview">
            <h2 className="eligibility-overview__header">
                <FormattedMessage id="eligibility_results.overview.header" values={{ coverageYear }} />
            </h2>
            {showSummaryHeader && <FormattedMessage id="eligibility_results.overview.summary" tagName='h3' />}
            {caseNumber && formattedApplicationDate &&
                <div className={'margin-t-20'}>
                    <FormattedMessage id="eligibility_results.overview.applicantId" values={{ caseNumber }} />
                    <br />
                    <FormattedMessage id="eligibility_results.overview.applicationDate" values={{ applicationDate: formattedApplicationDate }} />
                </div>
            }
            {
                orderedEligibilityListHasValue && Object.keys(orderedEligibilityList).sort().reverse().map((group, index) => {
                    let memberName = orderedEligibilityList[group][0].props.values.members;
                    let taxDetails, householdSize, householdIncome = null;


                    if (taxHouseholdDetails) {
                        taxDetails = taxHouseholdDetails.find(detail => detail && detail.taxHouseholdGroup === group);
                        if (taxDetails) {
                            householdSize = taxDetails.householdSize;
                            householdIncome = taxDetails.householdIncome;
                        }
                    }
                    return (
                        <Fragment key={index}>
                            {
                                group === 'NON_ITF' &&
                                <Fragment>
                                    {!isState.NJ &&
                                        <FormattedMessage
                                            id={isFinancial ? 'eligibility_results.overview.p1_financial'
                                                : 'eligibility_results.overview.p1_non_financial'
                                            }
                                            values={{
                                                members: memberName,
                                                strong: function makeStrong(chunks) {
                                                    return <strong>{chunks}</strong>;
                                                }
                                            }}
                                            tagName='p'
                                        />
                                    }
                                    {isState.NJ &&
                                        <FormattedMessage
                                            id={isFinancial ? 'eligibility_results.overview.p1_financial'
                                                : 'eligibility_results.overview.p1_non_financial'
                                            }
                                            values={{ members: memberName }}
                                            tagName='p'
                                        />
                                    }
                                </Fragment>

                            }
                            {
                                group !== 'NON_ITF' &&
                                <FormattedMessage
                                    id={isFinancial ?
                                        'eligibility_results.overview.p1_financial.itf'
                                        : 'eligibility_results.overview.p1_non_financial.itf'
                                    }
                                    values={{
                                        members: memberName,
                                        strong: function makeStrong(chunks) { return <strong>{chunks}</strong>; }
                                    }}
                                    tagName='p' />
                            }
                            <EligibilityOverviewList list={orderedEligibilityList[group]} />
                            {
                                isFinancial ?
                                    <Fragment>
                                        {householdSize > 0 &&
                                            <FormattedMessage
                                                id='eligibility_results.overview.p3.householdInfo'
                                                tagName='p'
                                                values={{
                                                    householdSize,
                                                    householdIncome: (
                                                        <Tooltip
                                                            id={formatDecimalNumber(householdIncome)}
                                                            isIdDynamicText={true}
                                                            content="eligibility_results.overview.p3.householdInfo.income.tooltip"
                                                            index={index}
                                                        />
                                                    ),
                                                    marketPlaceTooltip: (
                                                        <Tooltip
                                                            id="eligibility_results.overview.p3.householdInfo.marketPlaceTooltip"
                                                            content="eligibility_results.overview.p3.householdInfo.marketPlaceTooltip.content"
                                                            index={index}
                                                            replacement={{ exchangeName }}
                                                        />
                                                    ),
                                                    lineBreak: <br />,
                                                    exchangeName
                                                }}

                                            />
                                        }
                                        {householdSize === 0 && isState.NJ && <FormattedMessage id="eligibility_results.overview.p3" tagName='p' values={{ strong: chunks => <strong>{chunks}</strong> }} />}
                                    </Fragment> :
                                    <FormattedMessage id="eligibility_results.overview.non_financial" values={{ members: memberName }} tagName='p' />
                            }
                        </Fragment>
                    );
                })
            }
            <ConditionalRendering isTrue={isEligibilityMaintainTextVisible && isMaintainFATextVisible}>
                <p data-testid="eligibilityResultsMaintainFA"><FormattedMessage id="eligibility_results.overview.maintainFA" tagName='p' values={{ medicaidFullName, exchangeName, br: <br /> }} /></p>
            </ConditionalRendering>
            {isFinancial && !isState.NJ &&
                <Fragment>
                    <h3 className="eligibility-overview-is-financial__header mt-5 mb-0">
                        <FormattedMessage id="eligibility_results.overview.is_financial.header" />
                    </h3>
                    {additionalNote && <FormattedMessage id="eligibility_results.overview.p2" tagName='p' values={{ br: <br /> }} />}
                    {isMaintainFATextVisible && <p data-testid="eligibilityResultsMaintainFA"><FormattedMessage id="eligibility_results.overview.maintainFA" tagName='p' values={{ medicaidFullName, exchangeName, br: <br /> }} /></p>}
                    <ConditionalRendering isTrue={isBHPEligibleTextVisible}>
                        <div data-testid="eligibilityResultsBHP"><FormattedMessage id="eligibility_results.overview.bhpEligible" tagName='p' /></div>
                    </ConditionalRendering>
                    <div>
                        <FormattedMessage id="eligibility_results.overview.p3" tagName='p' values={{ strong: chunks => <strong>{chunks}</strong> }} />
                    </div>
                </Fragment>
            }
        </div>
    );
};

export default EligibilityOverview;